/**
 * Timeline store: holds the edit snapshot (docs/timeline-format.md), selection,
 * undo/redo. ALL mutations go through `mutate` / the transient-drag trio so
 * format invariants are enforced on every change.
 *
 * CRDT architecture: a Y.Doc (src/crdt/) mirrors the edit at field-level
 * granularity and is the collaborative source of truth. Local mutations still
 * produce plain immutable snapshots via immer (+ normalization); the keyed
 * reconciler then writes the snapshot transition into the doc. Changes that
 * originate IN the doc (undo/redo, remote peers, applied AI proposals) flow
 * the other way: materialize -> read-time repair (deriveRenderEdit) -> store.
 * Undo/redo is Y.UndoManager over local-origin transactions only.
 */
import { create } from 'zustand';
import { produce } from 'immer';
import * as Y from 'yjs';
import { deriveRenderEdit, manufacturedTransitions } from '../crdt/derive';
import { carrySourceUrls, materializeEdit } from '../crdt/materialize';
import { buildDocFromEdit, reconcileEdit } from '../crdt/reconcile';
import { shareEdit } from '../crdt/share';
import {
  AGENT_ORIGIN,
  LOCAL_ORIGIN,
  NO_UNDO_ORIGIN,
  ROOT_META,
  ROOT_SOURCES,
  ROOT_TRACKS,
} from '../crdt/schema';
import { clipDuration, clipEnd, emptyEdit, repairEditIds, totalDuration } from '../format/helpers';
import { normalizeAllTracks } from '../format/invariants';
import * as ops from '../format/ops';
import { findClip } from '../format/ops';
import type { ClipLocation } from '../format/ops';
import type { Op } from '../agent/core/types';
import { replayOps } from '../agent/proposal/replay';
import { replayRegistry } from '../agent/tools/catalog';
import { MUTATE_TOOLS } from '../agent/tools/edit';
import type {
  AnyClip,
  AudioClip,
  Edit,
  EditSettings,
  PandoraVideoSource,
  Source,
  TextClip,
  TextStyle,
  Track,
  TrackBlend,
  TransitionType,
  VideoClip,
} from '../format/types';

export { findClip };
export type { ClipLocation };

const UNDO_CAP = 100;

/**
 * The pure `apply` half of every mutating agent tool, for replaying a proposal
 * onto the live edit. Built once: the catalog is static.
 */
const AGENT_REPLAY_REGISTRY = replayRegistry(MUTATE_TOOLS);

/**
 * The CRDT document mirroring the edit, its undo manager, and the last
 * snapshot reconciled into it. Module-level (like the clipboard): exactly one
 * live doc per session, swapped wholesale by load(). `docMirror` is the
 * reconcile base and always mirrors the doc's exact content — which differs
 * from the store's `edit` right after an undo/remote update, when `edit` is
 * the read-time-repaired (derived) view.
 */
let doc: Y.Doc;
let undoMgr: Y.UndoManager;
let docMirror: Edit;
/** The three roots that make up the edit — see onDocUpdate's transaction guard. */
let timelineRoots: Y.Map<unknown>[] = [];

const INITIAL_EDIT = emptyEdit();

/**
 * Clipboard for copy/cut/paste. Module-level on purpose: it is NOT part of the
 * edit, NOT persisted, and NOT tracked by undo/redo. It also carries the Source
 * objects the copied clips reference so pasting into a different edit works.
 */
interface ClipboardPayload {
  clips: AnyClip[];
  sources: Record<string, Source>;
}
let clipboard: ClipboardPayload | null = null;

/** Payload for inserting a text clip from an external source (pandora text). */
export interface TextClipInsert {
  text: string;
  /** requested timeline start (s); resolved to the next free overlay slot */
  start: number;
  duration: number;
  style?: TextStyle;
  position?: { x: number; y: number };
  fadeIn?: number;
  fadeOut?: number;
}

interface TimelineState {
  edit: Edit;
  selection: string[];
  /** Undo/redo stack depths of the CRDT UndoManager (drives button state). */
  undoDepth: number;
  redoDepth: number;
  /** bumped on every committed (undoable) change — autosave listens to this */
  version: number;
  transientBase: Edit | null;
  /**
   * A concurrent merge produced an overlap and read-time repair manufactured
   * a crossfade nobody authored — by design, but worth telling the user
   * (docs/realtime-collab.md). Cleared by dismissMergeNotice.
   */
  mergeNotice: string | null;
  dismissMergeNotice(): void;
  /**
   * An action did less than the selection implied (e.g. detachAudio skipping
   * clips that were already muted). Cleared by dismissEditNotice.
   */
  editNotice: string | null;
  dismissEditNotice(): void;

  load(edit: Edit): void;
  mutate(fn: (edit: Edit) => void): void;
  beginTransient(): void;
  updateTransient(fn: (edit: Edit) => void): void;
  commitTransient(): void;
  cancelTransient(): void;
  undo(): void;
  redo(): void;

  select(id: string, additive?: boolean): void;
  setSelection(ids: string[]): void;
  clearSelection(): void;

  addVideoClip(source: PandoraVideoSource, inPoint: number, outPoint: number, at?: number): string;
  addAudioClip(source: PandoraVideoSource, inPoint: number, outPoint: number, at?: number): string;
  /**
   * Ripple-insert a sequence of video clips (e.g. an imported pan.do/ra Edit)
   * end-to-end starting at `at`, pushing existing clips right — one undo step.
   * A clip straddling `at` is split so the insert slots in cleanly.
   */
  insertEditClips(
    clips: { source: PandoraVideoSource; inPoint: number; outPoint: number }[],
    at: number,
    trackId?: string,
  ): string[];
  addTextClip(at: number): string;
  addTextClipFrom(opts: TextClipInsert): string;
  deleteClips(ids: string[]): void;
  /** Delete clips and close the freed gaps on each affected track (one undo). */
  rippleDelete(ids: string[]): void;
  /** Copy clips (+ their sources) to the module clipboard. No edit change. */
  copyClips(ids: string[]): void;
  /** Copy then delete (one undo for the delete). */
  cutClips(ids: string[]): void;
  /** Paste the clipboard so the group's earliest clip lands at `atPlayhead` (one undo). */
  pasteClips(atPlayhead: number): void;
  hasClipboard(): boolean;
  splitAt(t: number, ids: string[]): void;
  /**
   * Move each selected video clip's audio onto an audio track and mute the
   * video clip — one undo step. Selects the new audio clips.
   */
  detachAudio(ids: string[]): void;
  updateClip(id: string, patch: Partial<VideoClip> | Partial<TextClip> | Partial<AudioClip>): void;
  setClipTransition(id: string, type: TransitionType, duration: number): void;
  removeClipTransition(id: string): void;
  setSettings(patch: Partial<EditSettings>): void;
  setTrackMuted(trackId: string, muted: boolean): void;
  setTrackSolo(trackId: string, solo: boolean): void;

  /** Ephemeral per-kind target track for new clips (not persisted). */
  targetTrack: Partial<Record<Track['kind'], string>>;
  setTargetTrack(kind: Track['kind'], trackId: string | null): void;
  addTrack(kind: Track['kind'], afterTrackId?: string): string;
  removeTrack(trackId: string): void;
  /** Reorder within the same-kind block only (video draw order = array order). */
  moveTrack(trackId: string, dir: 1 | -1): void;
  renameTrack(trackId: string, label: string): void;
  setTrackGain(trackId: string, gain: number): void;
  setTrackBlend(trackId: string, blend: TrackBlend | null): void;
  setTrackOpacity(trackId: string, opacity: number): void;
  moveClipToTrack(clipId: string, targetTrackId: string): void;

  /**
   * Apply an assistant proposal's op log to the current timeline as ONE undoable
   * step. Replays the ops onto the live edit rather than applying a stored diff,
   * so a proposal built minutes ago still does the right thing (or reports an
   * honest conflict). See docs/agent.md §4.
   */
  applyAgentProposal(ops: Op[]): { ok: true } | { ok: false; conflict: string };

  /**
   * Keyframe ops. `paramPath` addresses a Param on the clip ('transform.scale',
   * 'effects.0.amount', 'position.x'); `atTimelineT` is TIMELINE time (usually
   * the playhead) — converted internally to the clip's keyframe axis.
   */
  setParamKeyframe(clipId: string, paramPath: string, value: number, atTimelineT: number): void;
  /** static → animated (1 keyframe at playhead); animated → static (bakes the sampled value). */
  toggleParamAnimated(clipId: string, paramPath: string, atTimelineT: number): void;
  removeParamKeyframe(clipId: string, paramPath: string, atTimelineT: number): void;
}

export const useTimeline = create<TimelineState>((set, get) => {
  /**
   * Run a mutation recipe against the current edit and commit it to the doc.
   * Returns whatever the recipe returned (ops report the ids they created, so
   * actions can set the selection to them) — immer runs the recipe exactly
   * once, so capturing its result out of the closure is safe.
   */
  function apply<T>(fn: (edit: Edit) => T, undoable: boolean): T {
    const { edit } = get();
    let produced!: T;
    // immer gives structural sharing: untouched tracks/clips keep their object
    // identity across snapshots, which lets the reconciler skip them cheaply.
    const next = produce(edit, (d) => {
      produced = fn(d);
      normalizeAllTracks(d.tracks);
    });
    if (next === edit) return produced;
    // docMirror (not `edit`) is the reconcile base: after an undo/remote
    // update the UI snapshot is the read-time-repaired view while the doc
    // still holds the raw merged state — diffing from the mirror writes those
    // repairs back into the doc alongside the user's change.
    reconcileEdit(doc, docMirror, next, undoable ? LOCAL_ORIGIN : NO_UNDO_ORIGIN);
    docMirror = next;
    if (undoable) set((s) => ({ edit: next, version: s.version + 1 }));
    else set({ edit: next });
    return produced;
  }

  /** Run an op, then select exactly what it created (the common action shape). */
  function applySelecting(fn: (edit: Edit) => ops.Produced): string[] {
    const { created } = apply(fn, true);
    if (created.length) set({ selection: created });
    return created;
  }

  return {
    edit: INITIAL_EDIT,
    selection: [],
    undoDepth: 0,
    redoDepth: 0,
    version: 0,
    transientBase: null,
    mergeNotice: null,
    editNotice: null,

    dismissMergeNotice() {
      set({ mergeNotice: null });
    },

    dismissEditNotice() {
      set({ editNotice: null });
    },

    load(edit) {
      const draft = structuredClone(edit) as Edit;
      // v1 documents are structurally valid v2 (params may be plain numbers);
      // upgrade in place so the next save persists version 2.
      if ((draft as { version: number }).version === 1) draft.version = 2;
      // legacy docs: duplicate ids from the old weak genId, effects without ids
      repairEditIds(draft);
      normalizeAllTracks(draft.tracks);
      initDoc(draft);
      set({
        edit: draft,
        selection: [],
        undoDepth: 0,
        redoDepth: 0,
        version: 0,
        transientBase: null,
        mergeNotice: null,
        editNotice: null,
      });
    },

    mutate(fn) {
      apply(fn, true);
    },

    beginTransient() {
      set({ transientBase: get().edit });
    },

    updateTransient(fn) {
      // transient states live ONLY in the snapshot — the doc is untouched
      // until commit, so a drag is one CRDT transaction and one undo step
      const base = get().transientBase;
      if (!base) return;
      const draft = produce(base, (d) => {
        fn(d);
        normalizeAllTracks(d.tracks);
      });
      set({ edit: draft });
    },

    commitTransient() {
      const { transientBase, edit } = get();
      if (!transientBase) return;
      if (transientBase === edit) {
        set({ transientBase: null });
        return;
      }
      reconcileEdit(doc, docMirror, edit, LOCAL_ORIGIN);
      docMirror = edit;
      set((s) => ({ version: s.version + 1, transientBase: null }));
    },

    cancelTransient() {
      const base = get().transientBase;
      if (base) set({ edit: base, transientBase: null });
    },

    // Undo/redo revert doc transactions with a tracked (local) origin; the
    // doc observer then flows the reverted state back into the snapshot.
    undo() {
      undoMgr.undo();
    },

    redo() {
      undoMgr.redo();
    },

    select(id, additive = false) {
      set((s) => {
        if (!additive) return { selection: [id] };
        return {
          selection: s.selection.includes(id)
            ? s.selection.filter((x) => x !== id)
            : [...s.selection, id],
        };
      });
    },

    setSelection(ids) {
      set({ selection: ids });
    },

    clearSelection() {
      set({ selection: [] });
    },

    addVideoClip(source, inPoint, outPoint, at) {
      const [id] = applySelecting((edit) =>
        ops.addVideoClip(edit, {
          source,
          in: inPoint,
          out: outPoint,
          at,
          targetTrack: get().targetTrack,
        }),
      );
      return id;
    },

    insertEditClips(clips, at, trackId) {
      return applySelecting((edit) =>
        ops.insertEditClips(edit, {
          clips: clips.map((c) => ({ source: c.source, in: c.inPoint, out: c.outPoint })),
          at,
          trackId,
          targetTrack: get().targetTrack,
        }),
      );
    },

    addAudioClip(source, inPoint, outPoint, at) {
      const [id] = applySelecting((edit) =>
        ops.addAudioClip(edit, {
          source,
          in: inPoint,
          out: outPoint,
          at,
          targetTrack: get().targetTrack,
        }),
      );
      return id;
    },

    addTextClip(at) {
      return get().addTextClipFrom({
        text: 'Title',
        start: Math.max(0, at),
        duration: 4,
      });
    },

    addTextClipFrom(opts) {
      const [id] = applySelecting((edit) =>
        ops.addTextClip(edit, { ...opts, targetTrack: get().targetTrack }),
      );
      return id;
    },

    deleteClips(ids) {
      if (!ids.length) return;
      apply((edit) => ops.deleteClips(edit, { clips: ids }), true);
      set((s) => ({ selection: s.selection.filter((id) => !ids.includes(id)) }));
    },

    rippleDelete(ids) {
      if (!ids.length) return;
      const idSet = new Set(ids);
      apply((edit) => ops.rippleDelete(edit, { clips: ids }), true);
      set((s) => ({ selection: s.selection.filter((id) => !idSet.has(id)) }));
    },

    copyClips(ids) {
      if (!ids.length) return;
      const { edit } = get();
      const idSet = new Set(ids);
      const clips: AnyClip[] = [];
      const sources: Record<string, Source> = {};
      for (const track of edit.tracks) {
        for (const c of track.clips as AnyClip[]) {
          if (!idSet.has(c.id)) continue;
          clips.push(structuredClone(c));
          if ('source' in c) {
            const srcId = (c as { source: string }).source;
            const s = edit.sources[srcId];
            if (s) sources[srcId] = structuredClone(s);
          }
        }
      }
      if (!clips.length) return;
      clipboard = { clips, sources };
    },

    cutClips(ids) {
      get().copyClips(ids);
      get().deleteClips(ids);
    },

    pasteClips(atPlayhead) {
      if (!clipboard || !clipboard.clips.length) return;
      const payload = clipboard;
      applySelecting((edit) =>
        ops.pasteClips(edit, {
          clips: payload.clips,
          sources: payload.sources,
          at: atPlayhead,
        }),
      );
    },

    hasClipboard() {
      return clipboard !== null && clipboard.clips.length > 0;
    },

    splitAt(t, ids) {
      applySelecting((edit) => ops.splitAt(edit, { at: t, clips: ids }));
    },

    detachAudio(ids) {
      if (!ids.length) return;
      const created = applySelecting((edit) =>
        ops.detachAudio(edit, { clips: ids, targetTrack: get().targetTrack }),
      );
      // The op skips non-video and already-muted clips. Silently doing less
      // than the selection asked for reads as a bug, so name it.
      const skipped = ids.length - created.length;
      set({
        editNotice: skipped
          ? `${skipped} of ${ids.length} clips skipped — already muted, or not video clips.`
          : null,
      });
    },

    updateClip(id, patch) {
      apply((edit) => ops.updateClip(edit, { clip: id, patch }), true);
    },

    setClipTransition(id, type, duration) {
      apply((edit) => ops.setClipTransition(edit, { clip: id, type, duration }), true);
    },

    removeClipTransition(id) {
      apply((edit) => ops.removeClipTransition(edit, { clip: id }), true);
    },

    setSettings(patch) {
      apply((edit) => ops.setSettings(edit, { patch }), true);
    },

    setTrackMuted(trackId, muted) {
      apply((edit) => ops.setTrackMuted(edit, { track: trackId, muted }), true);
    },

    setTrackSolo(trackId, solo) {
      apply((edit) => ops.setTrackSolo(edit, { track: trackId, solo }), true);
    },

    targetTrack: {},

    setTargetTrack(kind, trackId) {
      set((s) => ({ targetTrack: { ...s.targetTrack, [kind]: trackId ?? undefined } }));
    },

    addTrack(kind, afterTrackId) {
      const [id] = apply((edit) => ops.addTrack(edit, { kind, afterTrackId }), true).created;
      set((s) => ({ targetTrack: { ...s.targetTrack, [kind]: id } }));
      return id;
    },

    removeTrack(trackId) {
      const track = get().edit.tracks.find((t) => t.id === trackId);
      if (!track) return;
      const removedIds = new Set((track.clips as AnyClip[]).map((c) => c.id));
      apply((edit) => ops.removeTrack(edit, { track: trackId }), true);
      set((s) => ({
        selection: s.selection.filter((id) => !removedIds.has(id)),
        targetTrack: Object.fromEntries(
          Object.entries(s.targetTrack).filter(([, v]) => v !== trackId),
        ),
      }));
    },

    moveTrack(trackId, dir) {
      apply((edit) => ops.moveTrack(edit, { track: trackId, dir }), true);
    },

    renameTrack(trackId, label) {
      apply((edit) => ops.renameTrack(edit, { track: trackId, label }), true);
    },

    setTrackGain(trackId, gain) {
      apply((edit) => ops.setTrackGain(edit, { track: trackId, gain }), true);
    },

    setTrackBlend(trackId, blend) {
      apply((edit) => ops.setTrackBlend(edit, { track: trackId, blend }), true);
    },

    setTrackOpacity(trackId, opacity) {
      apply((edit) => ops.setTrackOpacity(edit, { track: trackId, opacity }), true);
    },

    moveClipToTrack(clipId, targetTrackId) {
      apply((edit) => ops.moveClipToTrack(edit, { clip: clipId, track: targetTrackId }), true);
    },

    applyAgentProposal(opsToApply) {
      const { edit } = get();
      const replayed = replayOps(edit, opsToApply, AGENT_REPLAY_REGISTRY);
      if (!replayed.ok) return { ok: false, conflict: replayed.conflict };

      const next = produce(replayed.edit, (d) => {
        normalizeAllTracks(d.tracks);
      });
      // AGENT_ORIGIN is tracked by the UndoManager (schema.ts), so the whole
      // proposal collapses into a single Cmd+Z.
      //
      // Deliberately NOT followed by `docMirror = next` / `set({edit: next})`,
      // which is what `apply()` does. `apply()` uses LOCAL_ORIGIN /
      // NO_UNDO_ORIGIN, and onDocUpdate skips those — so it must publish the
      // new state itself. AGENT_ORIGIN is NOT skipped: reconcileEdit's
      // transaction makes onDocUpdate fire synchronously, and it already sets
      // docMirror to the doc's materialised content and the store to the
      // read-time-repaired view. Re-assigning here overwrote both, leaving
      // docMirror holding something the document does not contain — and
      // docMirror is the reconcile base for every subsequent edit.
      reconcileEdit(doc, docMirror, next, AGENT_ORIGIN);
      return { ok: true };
    },

    setParamKeyframe(clipId, paramPath, value, atTimelineT) {
      apply(
        (edit) =>
          ops.setParamKeyframe(edit, { clip: clipId, param: paramPath, value, atTimelineT }),
        true,
      );
    },

    toggleParamAnimated(clipId, paramPath, atTimelineT) {
      apply(
        (edit) => ops.toggleParamAnimated(edit, { clip: clipId, param: paramPath, atTimelineT }),
        true,
      );
    },

    removeParamKeyframe(clipId, paramPath, atTimelineT) {
      apply(
        (edit) => ops.removeParamKeyframe(edit, { clip: clipId, param: paramPath, atTimelineT }),
        true,
      );
    },
  };
});

// ---------------------------------------------------------------------------
// CRDT doc lifecycle
// ---------------------------------------------------------------------------

/** (Re)create the Y.Doc + UndoManager for a freshly loaded edit. */
function initDoc(initial: Edit): void {
  if (undoMgr) undoMgr.destroy();
  if (doc) doc.destroy();
  doc = buildDocFromEdit(initial, NO_UNDO_ORIGIN);
  docMirror = initial;
  timelineRoots = [
    doc.getMap(ROOT_META),
    doc.getMap(ROOT_SOURCES),
    doc.getMap(ROOT_TRACKS),
  ];
  undoMgr = new Y.UndoManager(
    [doc.getMap(ROOT_META), doc.getMap(ROOT_SOURCES), doc.getMap(ROOT_TRACKS)],
    {
      // local edits and accepted AI proposals are undoable; remote peers'
      // transactions are not (per-user undo)
      trackedOrigins: new Set([LOCAL_ORIGIN, AGENT_ORIGIN]),
      // every transaction (= one store action / one drag commit) is one step
      captureTimeout: 0,
    },
  );
  undoMgr.on('stack-item-added', onUndoStackChange);
  undoMgr.on('stack-item-popped', onUndoStackChange);
  undoMgr.on('stack-cleared', onUndoStackChange);
  doc.on('update', onDocUpdate);
}

function onUndoStackChange(): void {
  if (undoMgr.undoStack.length > UNDO_CAP) {
    undoMgr.undoStack.splice(0, undoMgr.undoStack.length - UNDO_CAP);
  }
  useTimeline.setState({
    undoDepth: undoMgr.undoStack.length,
    redoDepth: undoMgr.redoStack.length,
  });
}

/**
 * The doc changed under us (undo/redo, applied AI proposal — later: remote
 * peers). Materialize the raw state, keep runtime urls, mirror it, and show
 * the read-time-repaired view. Local reconciles are skipped: for those the
 * snapshot is already current.
 */
function onDocUpdate(
  _update: Uint8Array,
  origin: unknown,
  _doc: Y.Doc,
  transaction: Y.Transaction,
): void {
  if (origin === LOCAL_ORIGIN || origin === NO_UNDO_ORIGIN) return;
  // The doc carries more than the edit: chat is a sibling root. Materializing
  // the whole timeline and re-rendering on every incoming chat message would be
  // pure waste, so ignore transactions that touched no timeline root.
  // `changedParentTypes` walks up from the modified type, so this still fires
  // for edits nested deep inside a clip.
  // The cast is Yjs's own variance quirk: a concrete Y.Map is not assignable to
  // the AbstractType<YEvent<any>> key type of changedParentTypes, but is a
  // valid key at runtime.
  const roots = transaction.changedParentTypes as unknown as Map<Y.Map<unknown>, unknown>;
  if (!timelineRoots.some((root) => roots.has(root))) return;
  const fresh = materializeEdit(doc);
  carrySourceUrls(useTimeline.getState().edit, fresh);
  // Share structure with the previous snapshots: materializing rebuilds every
  // object, but identity must only change where content did — memoized
  // components and the reconciler's identity fast-path both depend on it.
  const raw = shareEdit(docMirror, fresh);
  docMirror = raw;
  const derived = shareEdit(raw, deriveRenderEdit(raw));
  const conjured = manufacturedTransitions(raw, derived);
  useTimeline.setState((s) => ({
    edit: derived,
    version: s.version + 1,
    transientBase: null,
    selection: s.selection.filter((id) => findClip(derived, id)),
    ...(conjured.length
      ? {
          mergeNotice:
            `merged edits created a crossfade at ${conjured[0].toFixed(1)}s` +
            (conjured.length > 1 ? ` (+${conjured.length - 1} more)` : '') +
            ' — ⧓ badge to review',
        }
      : {}),
  }));
}

initDoc(INITIAL_EDIT);

/** The live Y.Doc — for the sync provider and tests. Replaced by load(). */
export function getTimelineDoc(): Y.Doc {
  return doc;
}

/**
 * Start a fresh, EMPTY doc for a collaborative session and hand it to the
 * caller to attach a sync provider to.
 *
 * The server is the only thing that seeds a room, so the client must not build
 * a doc from JSON first: two independent builders produce unrelated CRDT
 * histories, and merging them resolves whole id-keyed subtrees last-write-wins
 * rather than converging. The document arrives over the wire and lands through
 * the normal `onDocUpdate` path, exactly like a remote peer's edit.
 *
 * Callers must not render this as a real timeline until the provider reports
 * that it has synced — an empty doc and an empty project look identical.
 */
export function initEmptyDocForSync(): Y.Doc {
  initDoc(emptyEdit());
  useTimeline.setState({
    edit: emptyEdit(),
    selection: [],
    undoDepth: 0,
    redoDepth: 0,
    version: 0,
    transientBase: null,
    mergeNotice: null,
  });
  return doc;
}

/** Derived helpers for components. */
export function useTotalDuration(): number {
  return useTimeline((s) => totalDuration(s.edit));
}

export { clipDuration, clipEnd, totalDuration };
